import { Table as MantineTable, TableProps, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type TableVariant = 'default' | 'striped' | 'hoverable' | 'bordered' | 'minimal';

export interface ThemedTableProps extends Omit<TableProps, 'striped' | 'highlightOnHover' | 'withTableBorder' | 'withColumnBorders'> {
  tableVariant?: TableVariant;
}

interface TableComponent extends React.ForwardRefExoticComponent<ThemedTableProps & React.RefAttributes<HTMLTableElement>> {
  Thead: typeof MantineTable.Thead;
  Tbody: typeof MantineTable.Tbody;
  Tfoot: typeof MantineTable.Tfoot;
  Tr: typeof MantineTable.Tr;
  Th: typeof MantineTable.Th;
  Td: typeof MantineTable.Td;
  Caption: typeof MantineTable.Caption;
}

const Table = forwardRef<HTMLTableElement, ThemedTableProps>(
  ({ tableVariant = 'default', children, style, ...rest }, ref) => {
    const { colorScheme } = useMantineColorScheme();
    const theme = useMantineTheme();
    const isDark = colorScheme === 'dark';
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const variantProps: Record<TableVariant, Partial<TableProps>> = {
      default: {
        striped: false,
        highlightOnHover: false,
        withTableBorder: false,
        withColumnBorders: false,
      },
      striped: {
        striped: true,
        stripedColor: isDark ? theme.colors.dark[6] : theme.colors.gray[0],
        highlightOnHover: false,
        withTableBorder: false,
        withColumnBorders: false,
      },
      hoverable: {
        striped: false,
        highlightOnHover: true,
        highlightOnHoverColor: isDark ? theme.colors.dark[5] : theme.colors.gray[1],
        withTableBorder: false,
        withColumnBorders: false,
      },
      bordered: {
        striped: false,
        highlightOnHover: false,
        withTableBorder: true,
        withColumnBorders: true,
      },
      minimal: {
        striped: false,
        highlightOnHover: false,
        withTableBorder: false,
        withColumnBorders: false,
        verticalSpacing: 'xs',
        horizontalSpacing: 'xs',
      },
    };

    return (
      <MantineTable ref={ref} style={{ ...accessibilityStyles, ...style }} {...variantProps[tableVariant]} {...rest}>
        {children}
      </MantineTable>
    );
  }
) as TableComponent;

Table.displayName = 'Table';

// Attach subcomponents
Table.Thead = MantineTable.Thead;
Table.Tbody = MantineTable.Tbody;
Table.Tfoot = MantineTable.Tfoot;
Table.Tr = MantineTable.Tr;
Table.Th = MantineTable.Th;
Table.Td = MantineTable.Td;
Table.Caption = MantineTable.Caption;

export default Table;
